import { type FC, useCallback } from "react";

import { Flex, Stack } from "@mantine/core";
import { Button, Colors, Tooltip } from "@snapchat/snap-design-system";
import { CopyIcon, TrashIcon } from "@snapchat/snap-design-system-icons";

import { useBlizzardEvents } from "../../../analytics/hooks";
import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, type LensCreatorPayoutEligibility, type MyLensesLensStatus } from "../../../gql-generated/types";
import { PageHeader, PageSubHeader } from "../../../layout/components";
import { copyToClipboard } from "../../../shared/utils/browser";
import { type DeleteLensPermission } from "../../hooks/lens-delete";
import { useLensChangeTracker } from "../../hooks/useLensChangeTracker";
import { DetailsBreadcrumbsV3, LensPageBreadcrumbs, SaveChangesComponent, TemporaryTooltip } from "../shared";
import { DeleteLensButton } from "../shared/delete-lens";

import {
    copyButton,
    detailsHeaderContainer,
    removeLensButton,
    shareLensButton,
    stringsContainer,
} from "./LensHeader.styles";
import { ShareHostedLensButton } from "./ShareHostedLensButton";

const BREAKPOINT_HEADER = "md";

interface LensHeader {
    lensId: string;
    lensName: string;
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility;
    deeplink: string | undefined;
    deleteLensPermission: DeleteLensPermission;
    orgId: string;
    status: MyLensesLensStatus;
    unlockableId: number;
    isSpectaclesOnly: boolean;
    disableGenerateWebAR?: boolean;
    isSaveDisabled?: boolean;
}

export const LensHeader: FC<LensHeader> = ({
    lensId,
    lensName,
    lensCreatorPayoutEligibility,
    deeplink,
    orgId,
    deleteLensPermission: { isPermitted: isPermittedToDeleteLens, reason },
    isSpectaclesOnly,
    disableGenerateWebAR,
    isSaveDisabled = false,
}) => {
    const { sendBlizzardEvent } = useBlizzardEvents();
    const { hasChanges, saveLens, cancelChanges, updateLensLoading, updateLensError, getChangedFields } =
        useLensChangeTracker(lensId);

    const copyLensIdToClipboard = useCallback(() => {
        copyToClipboard(lensId);
    }, [lensId]);

    const copyDeeplinkToClipboard = useCallback(() => {
        if (!deeplink) {
            return;
        }
        copyToClipboard(deeplink);
        sendBlizzardEvent({
            event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
            pageType: "LENS_DETAILS",
            action: "SHARE_LENS",
        });
    }, [deeplink, sendBlizzardEvent]);

    const { configs } = useFeatureGates();
    const newUXenabled = configs[CofConfigId.MyLensesUxImprovements].enabled;
    const isHostedLensesEnabled = configs[CofConfigId.MyLensesHostedLenses].enabled;

    return (
        <Flex
            className={detailsHeaderContainer}
            direction={{ base: "column", [BREAKPOINT_HEADER]: "row" }}
            justify={{ base: "flex-start", [BREAKPOINT_HEADER]: "space-between" }}
            align={{ base: "flex-start", [BREAKPOINT_HEADER]: "flex-end" }}
            gap="xs"
            wrap="wrap"
        >
            <Stack
                className={stringsContainer}
                gap="xs"
                pb={{ base: "md", [BREAKPOINT_HEADER]: "lg" }}
                pr={{ base: 0, [BREAKPOINT_HEADER]: "md" }}
            >
                {newUXenabled ? <LensPageBreadcrumbs lensName={lensName} /> : <DetailsBreadcrumbsV3 />}
                <PageHeader>{lensName}</PageHeader>
                <PageSubHeader>
                    Lens ID: {lensId}
                    <TemporaryTooltip content="Lens ID copied to clipboard" callbackAction={copyLensIdToClipboard}>
                        <CopyIcon className={copyButton} fill={Colors.Gray.V300} />
                    </TemporaryTooltip>
                </PageSubHeader>
            </Stack>
            <Flex
                direction="row"
                align="flex-end"
                gap="sm"
                wrap="wrap"
                justify={{ base: "flex-start", [BREAKPOINT_HEADER]: "flex-end" }}
                pb={{ base: "md", [BREAKPOINT_HEADER]: "lg" }}
            >
                {hasChanges ? (
                    <SaveChangesComponent
                        hasChanges={hasChanges}
                        onSave={saveLens}
                        onCancel={cancelChanges}
                        loading={updateLensLoading}
                        isSaveDisabled={isSaveDisabled}
                        changedFields={getChangedFields()}
                        updateError={updateLensError}
                    />
                ) : (
                    <>
                        <DeleteLensButton
                            lensId={lensId}
                            lensCreatorPayoutEligibility={lensCreatorPayoutEligibility}
                            isDisabled={!isPermittedToDeleteLens}
                        >
                            {isPermittedToDeleteLens && !reason ? (
                                <Button shape="circle" className={removeLensButton}>
                                    <TrashIcon />
                                </Button>
                            ) : (
                                <Tooltip content={reason} placement="left">
                                    <div>
                                        <Button shape="circle" className={removeLensButton} isDisabled={true}>
                                            <TrashIcon />
                                        </Button>
                                    </div>
                                </Tooltip>
                            )}
                        </DeleteLensButton>

                        {deeplink && (
                            <TemporaryTooltip
                                content="Lens link copied to clipboard"
                                callbackAction={copyDeeplinkToClipboard}
                            >
                                <Button type="primary" className={shareLensButton}>
                                    Share Lens
                                </Button>
                            </TemporaryTooltip>
                        )}

                        {!isSpectaclesOnly && isHostedLensesEnabled && (
                            <ShareHostedLensButton orgId={orgId} lensId={lensId} isDisabled={disableGenerateWebAR} />
                        )}
                    </>
                )}
            </Flex>
        </Flex>
    );
};
